import React, { useRef, useState } from "react";
import styled from "styled-components";
import {
  Link,
  Route,
  Redirect,
  useLocation,
  useHistory,
  useParams,
} from "react-router-dom";
import { FaLock } from "react-icons/fa";
import LoginFrame from "../../components/LoginFrame";
import { useUser } from "../../auth";
import {
  Flex,
  Button,
  Text,
  FormInput,
  Input,
  ControlLabel,
  Box,
  typeSize,
} from "@coaching-culture/ui";
import Axios from "axios";
import { useForm } from "react-hook-form";
import axios from "axios";
import {
  passwordErrorsFromResponse,
  passwordLengthRule,
} from "../../passwordRules";

/** Message for a 429 from the auth endpoints (rate limit or account lock). */
const tooManyAttemptsMessage = (err: any): string | null =>
  err?.response?.status === 429
    ? (err.response.data?.error ??
      "Too many attempts. Please wait a few minutes and try again.")
    : null;

const OrLine = styled.p`
  display: flex;
  align-items: center;
  margin-bottom: 18px;
  color: ${(props) => props.theme.colors.textMuted};

  &:before,
  &:after {
    flex: 1;
    content: "";
    height: 1px;
    background: ${(props) => props.theme.colors.border};
    display: block;
    margin: 0 20px;
  }
`;

const ForgotPassword = styled(Link)`
  font-size: ${typeSize("small")};
`;

const LoginSection = ({ rootPath }: { rootPath: string }) => {
  const [user, setUser] = useUser();
  const location = useLocation<any>();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [otp, setOtp] = useState("");
  const [sso, setSso] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const { from } = location.state || { from: { pathname: rootPath } };

  if (user != null) {
    return <Redirect to={from.pathname} />;
  }

  const doLogin = (user) => {
    setUser(user.token);
  };

  const getLogin = (ev) => {
    ev.preventDefault();
    setError("");
    if (step === 0) {
      if (email === "") {
        setError("Email is required.");
        return;
      }
      setLoading(true);
      setSso(null);
      axios
        .get(`/api/sso/user?email=${encodeURIComponent(email)}`)
        .then(({ data }) => {
          if (data.saml === "require") {
            window.location = data.url;
          } else {
            setSso(data.url + `?redirect=${encodeURIComponent(from.pathname)}`);
            setLoading(false);
            setStep(1);
            setTimeout(() => {
              passwordRef.current?.focus();
            }, 200);
          }
        })
        .catch(() => {
          setLoading(false);
          setStep(1);
          setTimeout(() => {
            passwordRef.current?.focus();
          }, 200);
        });
    } else if (step === 1) {
      if (password === "") {
        setError("Password is required.");
        return;
      }
      Axios.post("/api/auth/token", { email, password })
        .then((x) => {
          if (x.data.requires2fa) {
            setStep(2);
          } else {
            doLogin(x.data);
          }
        })
        .catch((err) => {
          const tooMany = tooManyAttemptsMessage(err);
          if (tooMany != null) {
            setError(tooMany);
            return;
          }
          // 401 for a wrong email, wrong password or disabled account alike
          // (SEC-10); 403 is what older APIs sent for the last two.
          const status = Axios.isAxiosError(err) ? err.response?.status : null;
          if (status === 401 || status === 403) {
            setError("That email address or password wasn't recognised.");
            return;
          }
          setError("Something went wrong. Please try again.");
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (step === 2) {
      setLoading(true);
      Axios.post("/api/auth/2fa", { email, code: otp })
        .then(({ data }) => {
          doLogin(data);
          setLoading(false);
        })
        .catch((err) => {
          window.alert(
            tooManyAttemptsMessage(err) ??
              "Invalid or expired code. After 5 wrong codes you need to log in again for a new one.",
          );
          setLoading(false);
        });
    }
  };

  return (
    <>
      <form onSubmit={getLogin} style={{ width: "100%" }}>
        {step === 0 ? (
          <FormInput
            label="Email address"
            type="email"
            name="email"
            mb={3}
            disabled={loading}
            value={email}
            error={null}
            placeholder="Enter your email address..."
            onChange={(ev) => setEmail(ev.target.value)}
          />
        ) : step === 1 ? (
          <>
            {sso && (
              <>
                <Flex justifyContent="center" mb={4}>
                  <Button href={sso}>Login with SSO</Button>
                </Flex>
                <OrLine>Or</OrLine>
              </>
            )}
            <Flex flexDirection="column" mb={3}>
              <Flex justifyContent="space-between">
                <ControlLabel>Password</ControlLabel>
                <ForgotPassword to={rootPath + "forgot-password"}>
                  <FaLock size="0.8em" style={{ marginRight: 3 }} /> Forgot?
                </ForgotPassword>
              </Flex>
              <Input
                type="password"
                name="password"
                ref={passwordRef}
                placeholder="Enter your password"
                disabled={loading}
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
              />
            </Flex>
          </>
        ) : (
          <>
            <Text fontSize="section" fontWeight={600} color="black" mb={3}>
              Check your email for your two-factor code
            </Text>
            <FormInput
              label="OTP code"
              name="otp"
              mb={3}
              disabled={loading}
              value={otp}
              error={null}
              placeholder="Enter your OTP..."
              onChange={(ev) => setOtp(ev.target.value)}
            />
          </>
        )}
        <Flex mb={4} alignItems="center">
          <Button type="submit" disabled={loading} variant="primary" mr={3}>
            {loading ? "Please wait" : step === 0 ? "Continue" : "Sign in"}
          </Button>
          <Text color="dangerText" role="alert">
            {error}&nbsp;
          </Text>
        </Flex>
      </form>
    </>
  );
};

const ChangeTempPasswordSection = () => {
  const [user, , setUser] = useUser();
  const { register, errors, watch, handleSubmit } = useForm();

  const history = useHistory();
  const location = useLocation<any>();
  const { from } = location.state || { from: { pathname: "/" } };

  const onSubmit = (values) => {
    const spec = {
      password: values.password,
    };

    Axios.post("/api/auth/reset-password", spec)
      .then(() => {
        setUser({
          ...user,
          forcePasswordReset: false,
        });
        history.push(from.pathname);
      })
      .catch((err) => {
        window.alert(passwordErrorsFromResponse(err) ?? "There was an error.");
      });
  };

  if (user == null || !user.forcePasswordReset) {
    return <Redirect to="/" />;
  }

  return (
    <>
      <Text
        as="h1"
        color="black"
        fontSize="page"
        mb={2}
        fontWeight={600}
        textAlign="center"
      >
        Change your temporary password
      </Text>
      <Text as="p" mb={3} color="textMuted" textAlign="center">
        Enter your new password.
      </Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          label="Password"
          type="password"
          name="password"
          error={errors.password}
          ref={register({
            required: true,
            minLength: passwordLengthRule,
          })}
        />
        <FormInput
          label="Confirm password"
          type="password"
          name="confirmPassword"
          error={errors.confirmPassword}
          mb={4}
          ref={register({
            required: true,
            validate: (value) =>
              value === watch("password") ? null : "Must match password",
          })}
        />
        <Flex alignItems="center" mb={3} flexDirection="column">
          <Button type="submit" variant="primary" mb={2}>
            Reset password
          </Button>
        </Flex>
      </form>
    </>
  );
};

const ResetPasswordSection = () => {
  const { register, errors, watch, handleSubmit } = useForm();

  const [done, setDone] = useState(false);
  const location = useLocation();

  const q = new URLSearchParams(location.search);

  const token = q.get("token");

  const onSubmit = (values) => {
    const spec = {
      token,
      password: values.password,
    };

    Axios.post("/api/auth/reset-password", spec)
      .then(() => {
        setDone(true);
      })
      .catch((err) => {
        window.alert(
          passwordErrorsFromResponse(err) ?? "Invalid or expired reset token",
        );
      });
  };

  if (token == null || token === "") {
    return <Redirect to="/" />;
  }

  return (
    <>
      <Text as="h1" color="black" fontSize="page" mb={2} fontWeight={600}>
        Reset your password
      </Text>
      {done ? (
        <>
          <Text textAlign="center" mb={2}>
            Ok, your password has been reset. Please return to login and login
            with your new password.
          </Text>
          <Link to="/login">Back to login</Link>
        </>
      ) : (
        <>
          <Text as="p" mb={3} color="textMuted" textAlign="center">
            Enter your new password.
          </Text>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormInput
              label="Password"
              type="password"
              name="password"
              error={errors.password}
              ref={register({
                required: true,
                minLength: passwordLengthRule,
              })}
            />
            <FormInput
              label="Confirm password"
              type="password"
              name="confirmPassword"
              error={errors.confirmPassword}
              mb={4}
              ref={register({
                required: true,
                validate: (value) =>
                  value === watch("password") ? null : "Must match password",
              })}
            />
            <Flex alignItems="center" mb={3} flexDirection="column">
              <Button type="submit" variant="primary" mb={2}>
                Reset password
              </Button>
              <Link to="/login">Back to login</Link>
            </Flex>
          </form>
        </>
      )}
    </>
  );
};

const ForgotPasswordSection = ({ rootPath }: { rootPath: string }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const sendReminder = () => {
    setError("");
    if (email === "") {
      setError("Email is required");
      return;
    }

    Axios.post("/api/auth/forgot-password", { email })
      .then(() => {
        setDone(true);
      })
      .catch((err) => {
        setError(tooManyAttemptsMessage(err) ?? "Email not found");
      });
  };

  return (
    <>
      <Text as="h1" color="black" fontSize="page" mb={2} fontWeight={600}>
        Forgot your password?
      </Text>
      {done ? (
        <Box mb={3}>
          <Text mb={2}>
            If you have an account at Coaching Culture, we've sent you an email
            with instructions on resetting your password
          </Text>
          <Link to="/login">Back to login</Link>
        </Box>
      ) : (
        <>
          <Text as="p" mb={3} color="textMuted">
            Enter your email address and we'll send you a link to reset your
            password.
          </Text>
          <FormInput
            label="Email address"
            type="email"
            name="email"
            value={email}
            mb={4}
            error={null}
            onChange={(ev) => setEmail(ev.target.value)}
          />
          <Flex alignItems="center" mb={3} flexDirection="row">
            <Button onClick={sendReminder} mb={0} mr={3} variant="primary">
              Send reset email
            </Button>
            <Link to={rootPath + "login"}>Back to login</Link>
          </Flex>
          <Text color="dangerText" mb={3} role="alert">
            {error}&nbsp;
          </Text>
        </>
      )}
    </>
  );
};

const LoginPage = () => {
  const params = useParams<{ section?: string }>();

  const rootPath = params.section == null ? "/" : `/${params.section}/`;

  const getPathArray = (suffix: string): string[] =>
    ["/", "/:section/"].map((x) => x + suffix);

  return (
    <LoginFrame>
      <Route path={getPathArray("login")} exact>
        <LoginSection rootPath={rootPath} />
      </Route>
      <Route path={getPathArray("forgot-password")} exact>
        <ForgotPasswordSection rootPath={rootPath} />
      </Route>
      <Route path={getPathArray("reset-password")} exact>
        <ResetPasswordSection />
      </Route>
      <Route path={getPathArray("change-password")} exact>
        <ChangeTempPasswordSection />
      </Route>
    </LoginFrame>
  );
};

export default LoginPage;
